import React from 'react';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissionsSync } from '../contexts/PermissionsSyncContext';
import { setData, setSessionData } from '../redux/actions';
import {
  canAccessWithPermissions,
  canAccessAllActions,
  shouldSyncPermissionsOnLoad,
} from '../utils/permissions';
import { ROUTE_PERMISSIONS_ALL } from '../constants/menuPermissions';
import { getDefaultUserRoute } from '../utils/userRoutes';

const PermissionRoute = ({
  element,
  permissions = [],
  routePath = '',
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.data);
  const session = useSelector((state) => state.session.data);
  const { permissionsReady, permissions: syncedPermissions } =
    usePermissionsSync();
  const mustWaitForSync = shouldSyncPermissionsOnLoad(user);

  if (mustWaitForSync && !permissionsReady) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '50vh' }}
      >
        <Spin indicator={<LoadingOutlined spin />} size="large" />
      </div>
    );
  }

  const allowedAny =
    !permissions.length ||
    canAccessWithPermissions(
      user,
      null,
      permissions,
      permissionsReady ? syncedPermissions : null
    );

  const requiredAll = ROUTE_PERMISSIONS_ALL[routePath] || [];
  const allowedAll =
    !requiredAll.length ||
    canAccessAllActions(
      user,
      requiredAll,
      permissionsReady ? syncedPermissions : null
    );

  const allowed = allowedAny && allowedAll;

  if (!allowed) {
    const fallbackRoute = getDefaultUserRoute(
      user,
      permissionsReady ? syncedPermissions : null
    );
    const currentPath = location.pathname.replace(/\/$/, '') || '/';

    if (
      fallbackRoute !== '/access-denied' &&
      fallbackRoute !== currentPath
    ) {
      return <Navigate to={fallbackRoute} replace />;
    }

    return <Navigate to="/access-denied" replace />;
  }

  return React.cloneElement(element, {
    userData: user,
    setData: (payload) => dispatch(setData(payload)),
    setSessionData: (payload) => dispatch(setSessionData(payload)),
    session,
  });
};

export default PermissionRoute;
